import { safeToString } from "_shared/_utilities/safe-to-string";
import Guid from "_shared/guids/guid";
import { Change, diffLines, diffWords } from "diff";
import { TextChangeJSON } from "work/proposals/redlining/__diff/_schema/text-change-schema";
import { IRedlineableField } from "work/proposals/redlining/field-redlines/field-redline";

export interface TextChangeCollectionJSON {
  changes: TextChangeJSON[];
}

export class TextChangeCollection {
  public static fromJSONArray(jsonArray: Array<TextChangeJSON> | null | undefined): TextChangeCollection | undefined {
    if (!jsonArray?.length) return;
    const collection = new TextChangeCollection();
    jsonArray.forEach((json) => {
      collection.add(TextChange.fromJSON(json, collection));
    });
    if (collection.textChanges.length === 0) {
      throw new Error("TextChangeCollection has no changes in fromJSONArray.");
    }
    return collection;
  }

  public static fromRedlines<T extends IRedlineableField>(
    redlines: {
      originalEntry: T | null;
      revisedEntry: T | null;
      currentEntry: T | null | undefined;
    },
    options: { diffWords: boolean },
  ): TextChangeCollection | undefined {
    const { originalEntry, revisedEntry, currentEntry } = redlines;
    if (originalEntry === null && revisedEntry === null && !currentEntry) return;

    let { previous, next } = getPreviousAndNextFromEntries(revisedEntry, currentEntry, originalEntry);

    const diff = options.diffWords ? diffWords(previous, next) : diffLines(previous, next);
    if (!diff.length) return;
    const collection = new TextChangeCollection();
    diff.forEach((change) => {
      collection.add(new TextChange({ diff: change, parent: collection }));
    });
    if (collection.textChanges.length === 0) {
      throw new Error("TextChangeCollection has no changes in fromRedlines.");
    }
    return collection;
  }

  public get textChanges(): TextChange[] {
    return [...this._textChanges];
  }

  public constructor(textChanges: TextChange[] = []) {
    this._textChanges = [...textChanges];
  }

  public add(textChange: TextChange): void {
    this._textChanges.push(textChange);
  }
  public toJSON(): TextChangeCollectionJSON {
    return {
      changes: this.textChanges.map((change) => change.toJSON()),
    };
  }

  protected _textChanges: TextChange[] = [];
}

export default class TextChange {
  static fromJSON(json: TextChangeJSON, parent?: TextChangeCollection): TextChange {
    return new TextChange({ parent, diff: json.diff });
  }

  public readonly id = Guid.generate();
  public readonly parent: TextChangeCollection;
  public readonly diff: Change;

  public get index(): number {
    return this.parent.textChanges.indexOf(this);
  }
  public get isResolved(): boolean {
    return !this.diff.added && !this.diff.removed;
  }
  public get previousChange(): TextChange | undefined {
    return this.parent.textChanges[this.index - 1];
  }
  public get nextChange(): TextChange | undefined {
    return this.parent.textChanges[this.index + 1];
  }
  public get isModified(): boolean {
    return (
      ((this.diff.removed && this.nextChange?.diff.added) || (this.previousChange?.diff.removed && this.diff?.added)) ??
      false
    );
  }
  public get isAdded(): boolean {
    return (this.diff.added && !this.previousChange?.diff.added && !this.previousChange?.diff.removed) ?? false;
  }
  public get isRemoved(): boolean {
    return (this.diff.removed && !this.previousChange?.diff.added && !this.previousChange?.diff.removed) ?? false;
  }

  constructor(params: { parent?: TextChangeCollection; diff: Change }) {
    this.parent = params.parent ?? new TextChangeCollection();
    this.diff = params.diff;
  }

  public toJSON(): TextChangeJSON {
    return {
      diff: this.diff,
      isResolved: this.isResolved,
    };
  }
}

function getPreviousAndNextFromEntries<T extends IRedlineableField>(
  revisedEntry: T | null,
  currentEntry: T | null | undefined,
  originalEntry: T | null,
) {
  let previous = "";
  let next = "";

  if ((currentEntry === null && revisedEntry === null) || currentEntry?.isEqualTo(revisedEntry)) {
    previous = safeToString(originalEntry);
    next = safeToString(currentEntry);
  } else if (currentEntry || currentEntry === null) {
    previous = safeToString(revisedEntry);
    next = safeToString(currentEntry);
  } else {
    previous = safeToString(originalEntry);
    next = safeToString(revisedEntry);
  }

  return { previous, next };
}
